import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import CloseIcon from "@mui/icons-material/Close";
import PushPinIcon from "@mui/icons-material/PushPin";
import Box from "@mui/material/Box";
import ButtonBase from "@mui/material/ButtonBase";
import IconButton from "@mui/material/IconButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import {flexRender, type RowData, type Table as TanStackTable} from "@tanstack/react-table";
import {useId, useRef, useState} from "react";
import type {PointerEvent as ReactPointerEvent} from "react";
import {useTranslation} from "react-i18next";
import {CommonThemeColors} from "crqlar-react-common/dist/components/theme";
import {MAX_COLUMN_SIZE, MIN_COLUMN_SIZE} from "./constants";
import {getColumnAlignment, getPinnedColumnSx} from "./utils";

interface ServerTableHeaderProps<TData extends RowData> {
    table: TanStackTable<TData>
    sticky: boolean
}

interface ColumnResizeDrag {
    pointerId: number
    startX: number
    columnId: string
    /** Rendered header width at drag start — for the grow column this differs from getSize(). */
    startWidth: number
}

interface HeaderSortMenu {
    anchorEl: HTMLElement
    columnId: string
    label: string
}

const removableHeaderActionSx = {
    width: 24,
    height: 24,
    p: 0,
    mt: "-6px",
    borderRadius: 1,
    flexShrink: 0,
    "& .ServerTableHeaderAction-removeIcon": {display: "none"},
    "&:hover .ServerTableHeaderAction-defaultIcon, &:focus-visible .ServerTableHeaderAction-defaultIcon": {
        display: "none",
    },
    "&:hover .ServerTableHeaderAction-removeIcon, &:focus-visible .ServerTableHeaderAction-removeIcon": {
        display: "block",
    },
} as const;

const getColumnJustification = (alignment: "left" | "center" | "right") => {
    if (alignment === "right") return "flex-end";
    if (alignment === "center") return "center";
    return "flex-start";
};

export function ServerTableHeader<TData extends RowData>({
    table,
    sticky,
}: ServerTableHeaderProps<TData>) {
    const {t} = useTranslation();
    const resizeRef = useRef<ColumnResizeDrag | null>(null);
    const lastHandleTapRef = useRef<{columnId: string; time: number} | null>(null);
    const sortMenuId = useId();
    const [sortMenu, setSortMenu] = useState<HeaderSortMenu | null>(null);
    const leftHeaderGroups = table.getLeftHeaderGroups();
    const centerHeaderGroups = table.getCenterHeaderGroups();
    const rightHeaderGroups = table.getRightHeaderGroups();

    // Resize from rendered width because grow columns can differ from TanStack's stored size.
    const beginResize = (event: ReactPointerEvent<HTMLDivElement>, columnId: string) => {
        const headerCell = event.currentTarget.parentElement;
        if (!headerCell) return;

        event.preventDefault();
        event.stopPropagation();

        // Detect double-click here because preventDefault suppresses the native event.
        const now = Date.now();
        const lastTap = lastHandleTapRef.current;
        lastHandleTapRef.current = {columnId, time: now};
        if (lastTap && lastTap.columnId === columnId && now - lastTap.time < 350) {
            lastHandleTapRef.current = null;
            table.setColumnSizing(sizing => {
                const next = {...sizing};
                delete next[columnId];
                return next;
            });
            return;
        }

        resizeRef.current = {
            pointerId: event.pointerId,
            startX: event.clientX,
            columnId,
            startWidth: headerCell.getBoundingClientRect().width
        };
        // Pointer capture keeps resizing active outside the handle when the browser allows it.
        try {
            event.currentTarget.setPointerCapture(event.pointerId);
        } catch {
            // Moves over the handle still resize without capture.
        }

        // Body freezing and sizing persistence share this drag flag.
        table.setColumnSizingInfo(info => ({...info, isResizingColumn: columnId}));
    };

    const moveResize = (event: ReactPointerEvent<HTMLDivElement>) => {
        const drag = resizeRef.current;
        if (!drag || event.pointerId !== drag.pointerId) return;

        const nextWidth = Math.round(Math.min(
            Math.max(drag.startWidth + event.clientX - drag.startX, MIN_COLUMN_SIZE),
            MAX_COLUMN_SIZE
        ));
        table.setColumnSizing(sizing => ({...sizing, [drag.columnId]: nextWidth}));
    };

    const endResize = () => {
        if (!resizeRef.current) return;
        resizeRef.current = null;
        table.setColumnSizingInfo(info => ({...info, isResizingColumn: false}));
    };

    const applyHeaderSort = (descending: boolean) => {
        if (!sortMenu) return;

        table.setSorting([{id: sortMenu.columnId, desc: descending}]);
        setSortMenu(null);
    };

    return (
        <>
            <Box
                component="thead"
                sx={[
                    {
                        bgcolor: "grey.50",
                        "& > tr > th": {borderBottom: `1px solid ${CommonThemeColors.ref.neutral.neutral95}`},
                    },
                    sticky && {
                        position: "sticky",
                        top: 0,
                        zIndex: 2,
                    },
                ]}
            >
                {table.getHeaderGroups().map(headerGroup => {
                // Preserve the rendered left, center, and right pinning regions.
                const headers = [
                    ...(leftHeaderGroups[headerGroup.depth]?.headers ?? []),
                    ...(centerHeaderGroups[headerGroup.depth]?.headers ?? []),
                    ...(rightHeaderGroups[headerGroup.depth]?.headers ?? []),
                ];

                return (
                    <Box component="tr" key={headerGroup.id}>
                        {headers.map(header => {
                            const isPinned = header.column.getIsPinned() === "left";
                            const isServerSortable = header.column.columnDef.meta?.isServerSortable === true;
                            const sortDirection = header.column.getIsSorted();
                            const alignment = getColumnAlignment(header.column);
                            const label = typeof header.column.columnDef.header === "string"
                                ? header.column.columnDef.header
                                : header.column.columnDef.meta?.headerLabel ?? header.column.id;
                            const isSortMenuOpen = sortMenu?.columnId === header.column.id;
                            const headerLabel = (
                                <Box
                                    component="span"
                                    sx={{
                                        minWidth: 0,
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                    }}
                                >
                                    {flexRender(header.column.columnDef.header, header.getContext())}
                                </Box>
                            );

                            return (
                                <Box
                                    component="th"
                                    key={header.id}
                                    colSpan={header.colSpan}
                                    sx={{
                                        position: "relative",
                                        typography: "body1",
                                        fontWeight: 500,
                                        textAlign: alignment,
                                        py: 1,
                                        pl: 1,
                                        pr: 1,
                                        ...getPinnedColumnSx(header.column, sticky ? 3 : 2),
                                        bgcolor: "grey.50",
                                    }}
                                >
                                    {/* Column resizing is pointer-only and intentionally hidden on touch devices. */}
                                    {header.column.getCanResize() && (
                                        <Box
                                            onPointerDown={event => beginResize(event, header.column.id)}
                                            onPointerMove={moveResize}
                                            onPointerUp={endResize}
                                            onPointerCancel={endResize}
                                            onLostPointerCapture={endResize}
                                            onClick={event => event.stopPropagation()}
                                            sx={{
                                                position: "absolute",
                                                top: 0,
                                                bottom: 0,
                                                right: 0,
                                                width: 12,
                                                zIndex: 1,
                                                cursor: "col-resize",
                                                touchAction: "none",
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "flex-end",
                                                "@media (pointer: coarse)": {display: "none"},
                                                "&::after": {
                                                    content: '""',
                                                    width: "2px",
                                                    height: "55%",
                                                    borderRadius: 1,
                                                    bgcolor: header.column.getIsResizing() ? "primary.main" : "transparent",
                                                },
                                                "&:hover::after": {bgcolor: "primary.main"},
                                            }}
                                        />
                                    )}

                                    {header.isPlaceholder ? null : (
                                        <Box
                                            sx={{
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: getColumnJustification(alignment),
                                                gap: 0.5,
                                                width: "100%",
                                                minWidth: 0,
                                            }}
                                        >
                                            {isPinned && header.column.getCanPin() && (
                                                <Tooltip title={t("Unpin {{label}}", {label})} placement="bottom" arrow>
                                                    <IconButton
                                                        size="small"
                                                        aria-label={t("Unpin {{label}}", {label})}
                                                        onClick={event => {
                                                            event.stopPropagation();
                                                            header.column.pin(false);
                                                        }}
                                                        sx={removableHeaderActionSx}
                                                    >
                                                        <PushPinIcon
                                                            className="ServerTableHeaderAction-defaultIcon"
                                                            color="primary"
                                                            sx={{fontSize: 15}}
                                                        />
                                                        <CloseIcon
                                                            className="ServerTableHeaderAction-removeIcon"
                                                            color="primary"
                                                            sx={{fontSize: 15}}
                                                        />
                                                    </IconButton>
                                                </Tooltip>
                                            )}

                                            {isServerSortable ? (
                                                <ButtonBase
                                                    aria-label={`${t("Sort")}: ${label}`}
                                                    aria-haspopup="menu"
                                                    aria-expanded={isSortMenuOpen}
                                                    aria-controls={isSortMenuOpen ? sortMenuId : undefined}
                                                    onClick={event => {
                                                        event.stopPropagation();
                                                        setSortMenu({
                                                            anchorEl: event.currentTarget,
                                                            columnId: header.column.id,
                                                            label,
                                                        });
                                                    }}
                                                    sx={{
                                                        minWidth: 0,
                                                        maxWidth: "100%",
                                                        font: "inherit",
                                                        textAlign: "inherit",
                                                    }}
                                                >
                                                    {headerLabel}
                                                </ButtonBase>
                                            ) : headerLabel}

                                            {/* Hover swaps the sort indicator for removal without shifting layout. */}
                                            {sortDirection && (
                                                <Tooltip title={t("Remove sort")} placement="bottom" arrow>
                                                    <IconButton
                                                        size="small"
                                                        aria-label={t("Remove sort from {{label}}", {label})}
                                                        onClick={event => {
                                                            event.stopPropagation();
                                                            table.setSorting([]);
                                                        }}
                                                        sx={removableHeaderActionSx}
                                                    >
                                                        {sortDirection === "desc"
                                                            ? <ArrowDownwardIcon className="ServerTableHeaderAction-defaultIcon" color="primary" sx={{fontSize: 15}}/>
                                                            : <ArrowUpwardIcon className="ServerTableHeaderAction-defaultIcon" color="primary" sx={{fontSize: 15}}/>}
                                                        <CloseIcon
                                                            className="ServerTableHeaderAction-removeIcon"
                                                            color="primary"
                                                            sx={{fontSize: 15}}
                                                        />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </Box>
                                    )}
                                </Box>
                            );
                        })}

                    </Box>
                );
                })}
            </Box>

            <Menu
                id={sortMenuId}
                anchorEl={sortMenu?.anchorEl ?? null}
                open={!!sortMenu}
                onClose={() => setSortMenu(null)}
                MenuListProps={{
                    "aria-label": sortMenu ? `${t("Sort")}: ${sortMenu.label}` : t("Sort"),
                }}
            >
                <MenuItem onClick={() => applyHeaderSort(false)}>
                    <ListItemIcon>
                        <ArrowUpwardIcon fontSize="small"/>
                    </ListItemIcon>
                    {t("Sort ascending")}
                </MenuItem>
                <MenuItem onClick={() => applyHeaderSort(true)}>
                    <ListItemIcon>
                        <ArrowDownwardIcon fontSize="small"/>
                    </ListItemIcon>
                    {t("Sort descending")}
                </MenuItem>
            </Menu>
        </>
    );
}

export default ServerTableHeader;
